import React, { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
	CarIcon,
	SailboatIcon,
	VanIcon,
	IslandIcon,
	TractorIcon,
	TruckTrailerIcon,
} from "@phosphor-icons/react";
import {
	LOAN_PURPOSE_PERSONAL,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_CAR,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_TRUCK,
	LOAN_TYPE_PERSONAL,
	loanFieldsMap,
	emailTypos,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import { secureInput } from "@/lib/security";

interface Step1Props {
	loanType?: string;
}

export const PersonalDetails: React.FC<Step1Props> = ({ loanType }) => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		setFormErrors,
		setEnabledFields,
		enabledLoans,
		setValidateFormDetails,
		formContainer,
		toggleTC,
		setToggleTC,
		hasAttemptedSubmit,
	} = useFormStateStore();

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanType: false,
		loanAmount: false,
		tc: false,
	});

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const { firstName, lastName, email, loanAmount, loanType: zLoanType } = formValues;

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 480;
		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes
	const handleFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("firstName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("lastName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.email(value);
		setFormValue("email", sanitizedValue.sanitized);

		const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
		setFormError("email", emailErrorMsg);
	};

	const handleBlur = (field: "firstName" | "lastName" | "email", value: string) => {
		if (!value) return;

		if (field === "email") {
			setFormValue("email", value.toLowerCase());
		} else {
			// Capitalise first character, and convert ALL-CAPS strings to Capitalised
            const forcedCasing =
                value === value.toUpperCase()
                    ? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                    : value.charAt(0).toUpperCase() + value.slice(1);

            setFormValue(field, forcedCasing);
		}
	};

	const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
		setFormValue("loanAmount", formattedValue.toLocaleString());
		setErrors((prev) => ({
			...prev,
			loanAmount: formattedValue < loanAmountMin,
		}));
	};

	const handleLoanTypeChange = (value: string) => {
		setFormValue("loanType", value);
		setErrors((prev) => ({ ...prev, loanType: !value }));
		if (value) {
			setEnabledFields(value);
		}
		if (
			loanFieldsMap[value]?.loanPurpose[0].value === LOAN_PURPOSE_PERSONAL ||
			(loanFieldsMap[value]?.loanPurpose.length == 2 &&
				loanFieldsMap[value]?.loanPurpose[1].value === LOAN_PURPOSE_PERSONAL)
		) {
			setFormValue("abnStatus", "");
			setFormValue("gstStatus", "");
		}
	};

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			loanAmount: !loanAmount || parseFloat(loanAmount.replace(/,/g, "")) < loanAmountMin,
			loanType: !loanType,
			tc: !enabledTC,
		};

		setErrors(newErrors);
		setFormError("email", emailErrorMsg);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, loanAmount, zLoanType, loanType, enabledTC]);

	return (
		<div className="eF-space-y-3">
			<div>
				<label
					htmlFor="firstName"
					className="eF-text-base eF-font-semibold eF-leading-6 eF-text-gray-900"
				>
					Contact Details
				</label>
				<div
					className={clsx(
						isNavDivSmall
							? "eF-flex-col eF-gap-x-2 eF-gap-y-1"
							: "eF-flex-row eF-gap-x-2",
						"eF-flex",
					)}
				>
					<div className="eF-flex-1">
						<div className="eF-relative eF-mt-1">
							<input
								name="firstName"
								value={firstName}
								onChange={handleFirstNameChange}
								onBlur={(e) => handleBlur("firstName", e.target.value)}
								onFocus={() => handleFocus("firstName")}
								aria-label="First Name"
								type="text"
								id="firstName"
								placeholder="First Name"
								maxLength={30}
								autoComplete="given-name"
								className={clsx(
									"eF-capitalize",
									hasAttemptedSubmit && errors.firstName
										? "eF-ring-red-500 focus:eF-ring-red-500"
										: "eF-ring-gray-300 focus:eF-ring-[--eF-theme-color]",
									"eF-block eF-w-full eF-rounded-md eF-shadow-sm eF-border-0 eF-py-2.5 eF-text-gray-900 eF-ring-1 eF-outline-1 eF-outline-gray-900 focus:eF-ring-1 focus:eF-ring-inset focus-visible:eF-outline-[--eF-theme-color] eF-pl-3 placeholder:eF-text-gray-400 sm:eF-text-md sm:eF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.firstName && (
							<span className="eF-text-xs eF-text-red-500">First name required</span>
						)}
					</div>
					<div className="eF-flex-1">
						<div className="eF-relative eF-mt-1">
							<input
								name="lastName"
								value={lastName}
								onChange={handleLastNameChange}
								onBlur={(e) => handleBlur("lastName", e.target.value)}
								onFocus={() => handleFocus("lastName")}
								aria-label="Last Name"
								type="text"
								id="lastName"
								placeholder="Last Name"
								maxLength={30}
								autoComplete="family-name"
								className={clsx(
									"eF-capitalize",
									hasAttemptedSubmit && errors.lastName
										? "eF-ring-red-500 focus:eF-ring-red-500"
										: "eF-ring-gray-300 focus:eF-ring-[--eF-theme-color]",
									"eF-block eF-w-full eF-rounded-md eF-shadow-sm eF-border-0 eF-py-2.5 eF-text-gray-900 eF-ring-1 eF-outline-1 eF-outline-gray-900 focus:eF-ring-1 focus:eF-ring-inset focus-visible:eF-outline-[--eF-theme-color] eF-pl-3 placeholder:eF-text-gray-400 sm:eF-text-md sm:eF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.lastName && (
							<span className="eF-text-xs eF-text-red-500">
								Last name is required
							</span>
						)}
					</div>
				</div>

				<div className="eF-relative eF-mt-2">
					<div className="eF-pointer-events-none eF-absolute eF-inset-y-0 eF-left-0 eF-flex eF-items-center eF-pl-3">
						<EnvelopeIcon
							className="eF-h-5 eF-w-5 eF-text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						value={email}
						onChange={handleEmailChange}
						onBlur={(e) => handleBlur("email", e.target.value)}
						onFocus={() => {
							handleFocus("email");
							setFormError("email", "");
						}}
						aria-label="Email Address"
						type="email"
						id="email"
						placeholder="Email Address"
						maxLength={50}
						autoComplete="email"
						className={clsx(
							(hasAttemptedSubmit && errors.email) || formErrors.email
								? "eF-ring-red-500 focus:eF-ring-red-500"
								: "eF-ring-gray-300 focus:eF-ring-[--eF-theme-color]",
							"eF-block eF-w-full eF-rounded-md eF-shadow-sm eF-border-0 eF-py-2.5 eF-pl-10 eF-text-gray-900 eF-ring-1 eF-outline-1 eF-outline-gray-900 focus:eF-ring-1 focus:eF-ring-inset focus-visible:eF-outline-[--eF-theme-color] placeholder:eF-text-gray-400 sm:eF-text-md sm:eF-leading-6 eF-bg-none",
						)}
					/>
				</div>
				{formErrors.email && (
					<span className="eF-text-xs eF-text-red-500">{formErrors.email}</span>
				)}
			</div>

			{/* Loan Types */}
			{enabledLoans.length > 1 && (
				<RadioGroupField
					id="loanType"
					label="Choose a Loan Type"
					value={loanType || ""}
					options={enabledLoans.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_TYPE_CAR ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_BOAT ? (
								<SailboatIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_CARAVAN ? (
								<VanIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_PERSONAL ? (
								<IslandIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_TRUCK ? (
								<TruckTrailerIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanTypeChange(value);
					}}
					required={true}
					error={hasAttemptedSubmit && errors.loanType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			<div
				className={clsx(
					isNavDivSmall
						? "eF-flex-col eF-gap-x-2  eF-space-y-2"
						: "eF-flex-row eF-gap-x-2",
					"eF-flex",
				)}
			>
				<div className="eF-flex-1">
					<label
						htmlFor="loan-amount"
						className="eF-text-base eF-font-semibold eF-leading-6 eF-text-gray-900"
					>
						Loan Amount
					</label>
					<div className="eF-relative eF-mt-1">
						<div className="eF-pointer-events-none eF-absolute eF-inset-y-0 eF-left-0 eF-flex eF-items-center eF-pl-3">
							<span className="eF-text-gray-500 sm:eF-text-md">$</span>
						</div>
						<input
							value={loanAmount}
							onChange={handleLoanAmountChange}
							onFocus={() => handleFocus("loanAmount")}
							aria-label="Loan Amount"
							placeholder={
								loanAmountMin >= 10000
									? `Loan Amount (Min 10K)`
									: loanAmountMin == 5000
										? `Loan Amount (Min 5K)`
										: loanAmountMin == 2000
											? `Loan Amount (Min 2K)`
											: ``
							}
							maxLength={10}
							className={clsx(
								hasAttemptedSubmit && errors.loanAmount
									? "eF-ring-red-500 focus:eF-ring-red-500"
									: "eF-ring-gray-300 focus:eF-ring-[--eF-theme-color]",
								"eF-block eF-w-full eF-rounded-md eF-shadow-sm eF-border-0 eF-py-2.5 eF-pl-7 eF-pr-12 eF-text-gray-900 eF-ring-1 eF-ring-gray-300 focus:eF-ring-1 eF-outline-1 focus:eF-ring-inset focus-visible:eF-outline-[--eF-theme-color] placeholder:eF-text-gray-400 sm:eF-text-md sm:eF-leading-6",
							)}
							type="text"
							id="loan-amount"
							aria-describedby="loan-amount"
							inputMode="numeric"
						/>
						<div className="eF-pointer-events-none eF-absolute eF-inset-y-0 eF-right-0 eF-flex eF-items-center eF-pr-3">
							<span className="eF-text-gray-500 sm:eF-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.loanAmount && (
						<span className="eF-text-xs eF-text-red-500">
							Minimum loan amount is ${loanAmountMin.toLocaleString()}
						</span>
					)}
				</div>
			</div>
			<div id="tc-toggle" className={clsx("!eF-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{hasAttemptedSubmit && errors.tc && (
					<span className="eF-block eF-mt-1 eF-text-xs eF-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};
